export {};

declare global {
  /**
   * 拡張関数
   */
  interface Number {
    isValidNumber({ lower, upper }: { lower?: number; upper?: number }): boolean;
    addComma(): string;
    clamp(min: number, max: number): number;
    isDecimal(integerDigits: number, fractionalDigits: number): boolean;
    roundToDecimal(decimalPlaces: number): number;
    roundToDecimalPlacesNumber(decimalPlaces: number): number;
    divideIntegersToFixed(divider: number, decimalPlaces?: number): string;
  }
}

/**
 *　ある数値がnumber型であり、上限や下限の範囲内に収まっている有効な数値かどうかを判定する
 * @param limit - 有効範囲
 * @param limit.lower - 下限
 * @param limit.upper - 上限
 * @returns valueが有効な数値かどうか
 */
Number.prototype.isValidNumber = function ({
  lower = Number.MIN_SAFE_INTEGER,
  upper = Number.MAX_SAFE_INTEGER,
}: {
  lower?: number;
  upper?: number;
}) {
  const value = this as number;
  if (Number.isNaN(value)) {
    return false;
  }
  return value >= lower && value <= upper;
};

/**
 * 数値をカンマ区切りにする
 */
Number.prototype.addComma = function () {
  const value = this as number;
  if (value === undefined || value === null) return '';

  // 整数部分と小数部分を分離
  const [integerPart, decimalPart] = value.toString().split('.');
  // 整数部分にカンマを追加
  const formattedInteger = integerPart.replace(/(\d)(?=(\d{3})+$)/g, '$1,');

  // 小数部分があれば結合、なければそのまま
  return decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger;
};

/**
 * 数値を指定範囲内に収める
 * @param min - 最小値
 * @param max - 最大値
 * @returns 範囲内の数値
 */
Number.prototype.clamp = function (min: number, max: number) {
  const value = this as number;
  // minの方が大きい場合は入れ替える
  if (min > max) [min, max] = [max, min];
  return Math.min(Math.max(value, min), max);
};

/**
 * 数値が指定された桁数の小数点数かどうかを判定する
 * @param integerDigits - 整数部の桁数
 * @param fractionalDigits - 小数部の桁数
 */
Number.prototype.isDecimal = function (integerDigits: number, fractionalDigits: number) {
  const reg = new RegExp(`^\\d{1,${integerDigits}}(\\.\\d{1,${fractionalDigits}})?$`);
  return reg.test(String(this));
};

// TODO: どっちかに統一する
/**
 * 少数の桁数を任意の桁数に丸める
 * @param decimalPlaces - 任意の桁数
 */
Number.prototype.roundToDecimal = function (decimalPlaces: number): number {
  const value = this as number;
  if (value === undefined || value === null) return 0;
  const factor = Math.pow(10, decimalPlaces);
  return Math.round(value * factor) / factor;
};

/**
 * 少数の桁数を任意の桁数に丸める
 * @param decimalPlaces - 任意の桁数
 */
Number.prototype.roundToDecimalPlacesNumber = function (decimalPlaces: number): number {
  return parseFloat(this.toFixed(decimalPlaces));
};

/**
 * 小数を考慮した除算を行う
 * 整数部は整数で返す（整数判定は誤差が怖いため文字列で行う）
 * 小数点以下は指定された桁数で返す
 * @param divider - 除数
 * @param decimalPlaces - 小数点以下の桁数
 */
Number.prototype.divideIntegersToFixed = function (divider: number, decimalPlaces = 2): string {
  const value = this as number;
  if (divider === 0) {
    return '0.00';
  }

  // 桁上げして計算誤差を回避
  const factor = Math.pow(10, decimalPlaces);
  const numerator = Math.round(value * factor);
  const denominator = Math.round(divider * factor);
  const result = numerator / denominator;

  // toFixedでフォーマットした結果を取得
  const formattedResult = result.toFixed(decimalPlaces);

  // フォーマット後の文字列が小数点以下がすべて0かどうかを判定
  if (formattedResult.endsWith('.' + '0'.repeat(decimalPlaces))) {
    return parseInt(formattedResult, 10).toString(); // 整数として返す
  }

  // 小数点以下が0でない場合はそのまま返す
  return formattedResult;
};

/**
 * 入力されたオブジェクトから数値を返す(想定外の時は-1を変えす)
 * @deprecated 拡張関数で代用
 * @param inputStr - 入力
 * @returns 数値
 */
export const nullOrUndefinedToMinusOne = (inputStr?: string | number): number => {
  if (inputStr === undefined) return -1;
  let output = 0;
  if (typeof inputStr === 'string') {
    output = inputStr.parseNumber(); // カンマ区切りに対応
  } else {
    output = inputStr;
  }
  if (isNaN(output)) {
    return -1;
  }
  return output;
};

/**
 * 入力されたオブジェクトから数値を返す(想定外の時はundefinedを返す)
 * @deprecated 拡張関数で代用
 * @param inputStr - 入力
 * @returns 数値
 */
export const nullOrUndefined = (inputStr?: string | number): number | undefined => {
  if (inputStr === undefined) return 1;
  let output = 0;
  if (typeof inputStr === 'string') {
    output = inputStr.parseNumber(); // カンマ区切りに対応
  } else {
    output = inputStr;
  }
  if (isNaN(output)) {
    return undefined;
  }
  return output;
};
